<template>
	<div class="project-task-list-actions">
		<SuiActionBarToggleItem
			:label="selectedDoneFilterLabel"
			:helper-label="`${$t('task_list_show_tasks')}:`"
		>
			<template #default="{ closeDropdown }">
				<SuiListGroup
					:list-data="doneFilterItems"
					@click="closeDropdown"
				/>
			</template>
		</SuiActionBarToggleItem>

		<TaskListColumnToggle
			:storage-key="STORAGE_KEYS.PROJECT_TASK_LIST"
			:available-columns="projectTaskListAvailableColumns"
		/>

		<div
			v-if="canModifyProject || canCreateTasks"
			class="project-task-list-actions__group"
		>
			<SuiButton
				class="project-task-list-actions__button project-task-list-actions__button--add-task"
				variant="secondary"
				size="medium"
				@click="$emit('add-task')"
			>
				<SuiIcon name="icon-add-16" />
				{{ $t('task_list_add_task') }}
			</SuiButton>
			<AppDropdown
				:popover-props="{
					distance: 0,
					placement: 'bottom-end',
				}"
			>
				<template #trigger>
					<SuiButton
						class="project-task-list-actions__button project-task-list-actions__button--quick-actions"
						variant="icon"
						size="medium"
						:aria-label="$t('more_options')"
					>
						<SuiIcon name="icon-meatballs-16" />
					</SuiButton>
				</template>
				<TaskListActions
					:project="project"
					@open-phases-modal="$emit('open-phases-modal')"
				/>
			</AppDropdown>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiActionBarToggleItem,
	SuiButton,
	SuiIcon,
	SuiListGroup,
} from '@developers/scoro-ui';
import { usePermissions, useTranslation } from '@/composables';
import { type SuiListGroupData } from '@/types';
import { TASK_DONE_FILTER } from '@/features/Task/constants';
import { AppDropdown } from '@/features/App';
import {
	TaskListActions,
	TaskListColumnToggle,
	STORAGE_KEYS,
	useTaskListAvailableColumns,
} from '@/features/Task';
import { type TaskListProjectFragment } from '@/generated/graphql';
import { Constants } from '@/constants';
import { PermissionAction } from '@/constants/permissions';

export type ProjectTaskListActionsProps = {
	useTaskStatuses: boolean;
	useSubtasks: boolean;
	project?: TaskListProjectFragment;
};

const {
	project = undefined,
	useTaskStatuses,
	useSubtasks,
} = defineProps<ProjectTaskListActionsProps>();
defineEmits(['add-task', 'open-phases-modal']);
const selectedDoneFilter = defineModel<string>('selectedDoneFilter');

const { t } = useTranslation();
const { can } = usePermissions();
const { projectTaskListAvailableColumns } = useTaskListAvailableColumns({
	useTaskStatuses,
	useSubtasks,
});

const canModifyProject = computed(
	() => project?.permissions?.canModify || false
);

const canCreateTasks = computed(() =>
	can(PermissionAction.CREATE, Constants.Type.Task)
);

const doneFilterItems = computed<SuiListGroupData[]>(() => [
	{
		id: 'done-filter-items',
		items: [
			{
				id: TASK_DONE_FILTER.Done,
				title: t('task_done'),
				active: selectedDoneFilter.value === TASK_DONE_FILTER.Done,
				click: () => (selectedDoneFilter.value = TASK_DONE_FILTER.Done),
			},
			{
				id: TASK_DONE_FILTER.NotDone,
				title: t('not_done_only'),
				active: selectedDoneFilter.value === TASK_DONE_FILTER.NotDone,
				click: () =>
					(selectedDoneFilter.value = TASK_DONE_FILTER.NotDone),
			},
			{
				id: TASK_DONE_FILTER.All,
				title: t('done_and_not_done'),
				active: selectedDoneFilter.value === TASK_DONE_FILTER.All,
				click: () => (selectedDoneFilter.value = TASK_DONE_FILTER.All),
			},
		],
	},
]);

const selectedDoneFilterLabel = computed(() => {
	switch (selectedDoneFilter.value) {
		case TASK_DONE_FILTER.Done:
			return t('task_done');
		case TASK_DONE_FILTER.NotDone:
			return t('not_done_only');
		default:
			return t('done_and_not_done');
	}
});
</script>

<style lang="scss">
.project-task-list-actions {
	--sui-list-item-title-size: 1.5rem;

	display: flex;
	gap: 1rem;

	&__group {
		display: flex;
	}

	&__button {
		--sui-button-background-color: #{$neutral-10};
		--sui-button-hover-background-color: #{$neutral-20};
		--sui-button-icon-color: #{$neutral-60};

		&--add-task {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}

		&--quick-actions {
			border-left: 1px solid $neutral-20;
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}
}
</style>
